/* eslint-disable react/prop-types */
import { useLayoutEffect, useRef, useState } from 'react';

// The top bar's segmented switch (first built for Assisted / Portal / Guide / All,
// SecondBandSwitch.jsx), shared so other switches look and move the same: a white thumb
// slides behind the lit option.
// options: [{ value, label }] (label can be a node); onChange(value) runs for a different option.
const SegmentedSwitch = ({ options, value, onChange, ariaLabel, title, disabled, className = '' }) => {
  const buttons = useRef({});
  const [thumb, setThumb] = useState(null);

  // Measured again whenever a button changes size (the web font loading late, the bold
  // active label, or a count changing), so the thumb always sits exactly behind its label.
  useLayoutEffect(() => {
    const measure = () => {
      const el = buttons.current[value];
      setThumb(el ? { left: el.offsetLeft, width: el.offsetWidth } : null);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    Object.values(buttons.current).forEach(el => el && ro?.observe(el));
    document.fonts?.ready?.then(measure);
    return () => ro?.disconnect();
  }, [value]);

  return (
    <div
      role='radiogroup'
      aria-label={ariaLabel}
      title={title}
      className={`relative inline-flex items-center gap-0.5 rounded-[10px] bg-base-100 p-[3px] dark:bg-base-700 ${className}`}
    >
      <span
        aria-hidden='true'
        className='absolute top-[3px] bottom-[3px] rounded-lg bg-white shadow-sm ring-1 ring-black/5 transition-all duration-200 ease-out dark:bg-base-500'
        style={thumb ? { left: thumb.left, width: thumb.width, opacity: 1 } : { left: 3, width: 0, opacity: 0 }}
      />
      {options.map(option => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            ref={el => (buttons.current[option.value] = el)}
            type='button'
            role='radio'
            aria-checked={on}
            data-switch-value={option.value}
            disabled={disabled}
            onClick={() => !on && onChange(option.value)}
            className={`relative z-10 rounded-lg px-3 py-1 text-xs transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-wait ${
              on
                ? 'font-semibold text-base-900 dark:text-white'
                : 'font-medium text-base-500 hover:text-base-800 dark:text-base-300 dark:hover:text-white'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedSwitch;
